import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import styles from './lessonPlan.module.less';
import AccountContext from '../../services/AccountContext';
import { Remarkable } from 'remarkable';

const LessonPlanSection = ({
  lessonPlanData,
  sectionName,
  title,
  isELField = true,
  onlyForPro = true,
  onlyForLoggedIn = true,
}) => {
  const meta = lessonPlanData.meta;
  const urlName = lessonPlanData.urlName;
  const showELContent = lessonPlanData.showELContent;
  const handleMainAction = lessonPlanData.handleMainAction;

  const { memberId, isPremium } = useContext(AccountContext);

  const md = new Remarkable('default', {
    html: true,
    linkTarget: '_blank',
  });

  let attachmentSections = {
    introduction: [],
    elIntroduction: [],
    explicitInstruction: [],
    elExplicitInstruction: [],
    independentWorkingTime: [],
    elIndependentWorkingTime: [],
    guidedPractice: [],
    elGuidedPractice: [],
    assessment: [],
    elAssessment: [],
  };

  // Handle attachments' thumbnails
  if (Object.keys(attachmentSections).includes(sectionName)) {
    for (let i = 0; i < Object.keys(attachmentSections).length; i++) {
      let curKey = Object.keys(attachmentSections)[i];
      if (meta.body.attachments) {
        let j = 0;
        meta.body.attachments.forEach(attachment => {
          if (attachment.section && attachment.section === curKey) {
            let imgClass =
              j === 0 || (j === 1 && meta.body.attachments.length % 2 === 0)
                ? styles.large
                : '';
            let filenameArr = attachment.filename.split('.');
            let extension = filenameArr[filenameArr.length - 1].toUpperCase();
            let imgHtml =
              extension === 'PDF' ? (
                <img
                  src={
                    'https://cdn.education.com/files/static/lesson-plan-page/thumbnails/' +
                    urlName +
                    '__' +
                    attachment.filename.replace('.', '-') +
                    '.png'
                  }
                  alt={attachment.title}
                />
              ) : (
                <div className={styles.icon}>
                  <div
                    className={clsx(
                      styles.page,
                      styles['attachment' + extension]
                    )}
                  >
                    {extension}
                    <div>{attachment.title}</div>
                  </div>
                </div>
              );

            attachmentSections[curKey] = attachmentSections[curKey].concat(
              <button
                onClick={event =>
                  handleMainAction(
                    event,
                    '/download/lesson-plan/' +
                      urlName +
                      '/attachments/' +
                      attachment.filename
                  )
                }
                className={clsx(
                  'download-link lesson-plan-attachment',
                  styles.thumbnail,
                  imgClass
                )}
                aria-label={attachment.title}
                key={attachment.filename}
              >
                {imgHtml}
              </button>
            );

            j++;
          }
        });
      }
    }
  }

  let sectionData = meta['body'][sectionName];
  let hFlag = 4;
  if (
    meta['body']['elObjectives'] &&
    (sectionName === 'academicObjectives' ||
      sectionName === 'languageObjectives')
  ) {
    sectionData = meta['body']['elObjectives'][sectionName];
    hFlag = 5;
  }

  if (!sectionData) {
    return null;
  }

  if (onlyForLoggedIn && !memberId) {
    return null;
  }

  if (onlyForLoggedIn && onlyForPro && memberId && !isPremium) {
    return null;
  }

  let timeHtml = sectionData['time'] ? (
    <em className={styles.sectionTime}>({sectionData['time']} minutes)</em>
  ) : null;

  let sectionImgHtml = attachmentSections[sectionName] ? (
    <div className={styles.sectionImg}>{attachmentSections[sectionName]}</div>
  ) : null;

  let elContent =
    isELField && showELContent && sectionData['elText'] ? (
      <div className={styles.elContent}>
        <div className={styles.elLabel}>
          <i className={styles.iconEL} />
        </div>
        <div
          className={styles.elContentMain}
          dangerouslySetInnerHTML={{
            __html: md.render(sectionData['elText']),
          }}
        />
      </div>
    ) : null;

  let text = sectionData['text'] ? sectionData['text'] : sectionData;

  if (sectionName === 'proficiency' && text.length > 0) {
    text = '*' + sectionData.join(' *');
  }

  if (
    sectionName === 'word' ||
    sectionName === 'sentence' ||
    sectionName === 'discourse'
  ) {
    text = sectionData['content'];
  }

  // For some cases, text object can have valid properties {text, elText}, but both of the properties hold empty values.
  // And we don't want anything displayed for the section if that's the case, added (text.text && text.text !== '')
  // condition to make sure we catch this case. For other cases, text objects just have {key: value} pairs in the format
  // {index: character}.
  if (
    text.length > 0 ||
    (Object.keys(text).length > 0 &&
      (text.text === undefined || (text.text && text.text !== '')))
  ) {
    let titleHtml =
      hFlag === 4 ? (
        <h4 className={styles.navHeading}>{title}</h4>
      ) : (
        <h5 className={styles.navHeading}>{title}</h5>
      );
    return (
      <section id={title}>
        <div>
          {titleHtml}
          {timeHtml}
        </div>
        {sectionImgHtml}
        {typeof text === 'string' ? (
          <div dangerouslySetInnerHTML={{ __html: md.render(text) }} />
        ) : (
          ''
        )}
        {elContent}
      </section>
    );
  } else {
    return null;
  }
};

LessonPlanSection.propTypes = {
  lessonPlanData: PropTypes.shape({
    meta: PropTypes.string,
    urlName: PropTypes.string,
    showElContent: PropTypes.bool,
    handleMainAction: PropTypes.func,
  }),
  sectionName: PropTypes.string,
  title: PropTypes.string,
  isELField: PropTypes.bool,
  onlyForPro: PropTypes.bool,
  onlyForLoggedIn: PropTypes.bool,
};

export default LessonPlanSection;
